import PolicyLayout, { PolicyCallout, PolicyList, PolicySection } from "@/pages/policies/components/PolicyLayout";

export default function TermsPage() {
  return (
    <PolicyLayout
      eyebrow="Policies"
      title="Terms of Service"
      updated="1 October 2026"
      intro="These terms explain how Gari works and what we expect from every rider and host. By booking a car or listing one on Gari you agree to them. Prices are shown in Kenyan Shillings (KSh) unless stated otherwise."
    >
      <PolicySection id="about" title="1. About these terms">
        <p>
          Gari is a peer-to-peer car rental marketplace that connects vehicle owners (hosts) with people who want to
          rent a car (riders) across Kenya. Gari provides the platform, the booking tools, payments and support — the
          rental agreement itself is between the host and the rider.
        </p>
        <p>
          These terms apply to everyone who uses Gari. If you use Gari on behalf of a company or another person, you
          confirm you have the authority to accept them.
        </p>
      </PolicySection>

      <PolicySection id="eligibility" title="2. Who can use Gari">
        <PolicyList
          items={[
            "You must be at least 18 years old and hold a valid driving licence recognised in Kenya.",
            "You must give accurate personal details, including your name, phone number and, where required, a valid means of identification.",
            "You are responsible for making sure you are legally allowed and insured to drive the specific car you book.",
            "Hosts must be the registered owner of the vehicle, or have the owner's written permission to list it.",
          ]}
        />
      </PolicySection>

      <PolicySection id="account" title="3. Your Gari account">
        <p>
          You are responsible for everything that happens under your account. Keep your password and phone secure, and
          contact us immediately if you think someone else has access. Allowing another person to book or drive under
          your account breaches these terms and may void any protection.
        </p>
      </PolicySection>

      <PolicySection id="bookings" title="4. Bookings & pricing">
        <p>
          When you request a booking we show a full price breakdown before you pay — the daily rate multiplied by the
          number of days, plus a service fee. The host may accept or decline the request. A booking is confirmed only
          once the host has accepted it and payment has been verified.
        </p>
        <PolicyCallout icon="ri-price-tag-3-line">
          Prices are set by hosts. Gari&apos;s service fee is added at checkout and shown separately, so there are no
          hidden surprises.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="payments" title="5. Payments">
        <p>
          Gari supports local payment, including M-Pesa. Payment is collected when you complete checkout. Your payment
          is verified before the trip begins, and released to the host once the trip is completed without an open
          dispute.
        </p>
        <p>
          If the car is materially different from its listing, or unsafe to drive, report it before or at the start of
          the trip so we can help before the rental proceeds.
        </p>
      </PolicySection>

      <PolicySection id="rider-duties" title="6. Your responsibilities as a rider">
        <PolicyList
          items={[
            "Use the car only for the agreed dates and within Kenya unless the host has agreed otherwise.",
            "Return the car on time, at the agreed location, with the same fuel level and in the condition you received it.",
            "Do not let anyone else drive unless they are an approved additional driver.",
            "Follow Kenyan traffic law at all times, including speed limits, seat belts and phone-use rules.",
            "Report any accident, damage, theft or breakdown to the host and Gari as soon as it is safe to do so.",
          ]}
        />
      </PolicySection>

      <PolicySection id="host-duties" title="7. Your responsibilities as a host">
        <PolicyList
          items={[
            "Describe your car accurately — make, model, year, features, condition and any faults.",
            "Keep the car roadworthy, taxed and legally insured as a private vehicle, with a valid inspection where required.",
            "Hand the car over clean, with the agreed fuel level, on time and at the agreed place.",
            "Honour confirmed bookings and respond to requests promptly.",
            "Declare any custom demands or restrictions clearly before a rider books.",
          ]}
        />
      </PolicySection>

      <PolicySection id="checkin" title="8. Vehicle condition & check-in videos">
        <p>
          Because the rental is between two people, condition matters. Gari lets riders and hosts record a camera
          walkaround of the car before pickup and after the ride. These clips are stored privately on the booking and
          can be compared side by side if a disagreement arises.
        </p>
        <p>
          Either party can flag damage with a note, which raises a dispute on the booking. Hosts and riders are asked to
          review and resolve disputes in good faith. Documenting the car at handover materially strengthens any
          protection claim.
        </p>
      </PolicySection>

      <PolicySection id="reviews" title="9. Reviews">
        <p>
          Riders can leave a rating and a written review after a trip. Reviews must reflect a genuine experience. We
          remove reviews that are abusive, discriminatory, defamatory, or that expose personal information.
        </p>
      </PolicySection>

      <PolicySection id="cancellations" title="10. Cancellations, refunds & protection">
        <p>
          Cancellations and refunds are governed by our Cancellation Policy, and any protection you selected is governed
          by our Protection Plans. In summary: cancel early for a full refund, expect a partial refund closer to the
          start time, and always document the car.
        </p>
      </PolicySection>

      <PolicySection id="prohibited" title="11. Prohibited uses">
        <p>
          A Gari car may never be used for anything listed in our Guest Standards. That includes off-road driving,
          racing, towing, driving without a licence, subletting, and any unlawful or commercial-for-hire use. Breaching
          these rules can void your protection and end your access to Gari.
        </p>
      </PolicySection>

      <PolicySection id="termination" title="12. Suspension & termination">
        <p>
          We may pause or remove an account that repeatedly cancels, breaches these terms, receives credible safety
          complaints, or attempts fraud. Where possible we explain why and give you a chance to respond.
        </p>
      </PolicySection>

      <PolicySection id="liability" title="13. Disclaimers & liability">
        <p>
          Gari is a marketplace. We do not own the cars and we are not the rental party. We work hard to verify users and
          keep the platform safe, but we cannot guarantee the behaviour of every user or the condition of every vehicle.
        </p>
        <p>
          Protection plans are provided in partnership with licensed insurers and are subject to their terms, limits and
          exclusions. Nothing in these terms limits any rights you have that cannot be limited under Kenyan law.
        </p>
      </PolicySection>

      <PolicySection id="law" title="14. Governing law">
        <p>
          These terms are governed by the laws of Kenya. Any dispute that cannot be resolved with our support team will
          be subject to the courts of Kenya.
        </p>
      </PolicySection>

      <PolicySection id="changes" title="15. Changes & contact">
        <p>
          We may update these terms from time to time. When we do, we update the date at the top of this page and, for
          significant changes, notify you in the app. Questions? Reach our support team any time from the help section
          of your account.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}